// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { useCallback } from 'react';
import type { CSSProperties, RefObject } from 'react';

import { useCreateDevUserSession } from '@jane/kiosk/data-access';
import { useKioskSpacing } from '@jane/kiosk/hooks';
import type { MenuCategory } from '@jane/kiosk/util';
import type { Kiosk, Store } from '@jane/shared/types';

import { MenuCategories } from '../MenuCategories/MenuCategories';
import { StoreSwitcher } from '../StoreSwitcher/StoreSwitcher';
import { ThemedBackground } from '../ThemedBackground';
import { HeaderIcons } from './HeaderIcons';
import { SignInButton } from './SignInButton';
import { SignInPrompt } from './SignInPrompt';

export interface MenuHeaderProps {
  cart?: Nullable<{ products?: { count: number }[] }>;
  categoriesRef?: RefObject<HTMLDivElement | null>;
  currentCategory: MenuCategory;
  isJaneMode: boolean;
  kioskName?: string | null;
  onCartButtonClick: () => void;
  onCategoryPress: (category: MenuCategory) => void;
  onLoginIconClick: (optionSelected: 'login icon' | 'sign in prompt') => void;
  onLogoutIconClick: () => void;
  onSearchIconClick: () => void;
  onStoreSwitchClick: () => void;
  signedIn: boolean;
  store: Store;
  stores?: Kiosk['stores'];
  visibleTabs: MenuCategory[];
}

const StyledContainer = styled.div({
  backgroundColor: 'transparent',
  flexDirection: 'column',
  left: 0,
  paddingLeft: 0,
  paddingRight: 0,
  position: 'sticky' as const,
  right: 0,
  top: 0,
  width: '100vw',
  zIndex: 'var(--layers-header)',
  pointerEvents: 'none',
});

const topHeaderStyle = (includeStoreSwitcher: boolean): CSSProperties => ({
  paddingBottom: 16,
  paddingTop: includeStoreSwitcher ? 16 : 48,
  position: 'relative' as const,
  pointerEvents: 'auto',
  zIndex: 2,
});

export const MenuHeader = ({
  cart,
  categoriesRef,
  isJaneMode,
  kioskName,
  signedIn,
  store,
  stores,
  onLoginIconClick,
  onLogoutIconClick,
  onStoreSwitchClick,
  onSearchIconClick,
  onCartButtonClick,
  onCategoryPress,
  ...menuCategoriesProps
}: MenuHeaderProps) => {
  const showSignIn =
    store?.kiosk_anonymous_checkout !== 'required' && store?.kiosk_allow_auth;
  const { isIpad, getKioskSpacing } = useKioskSpacing();

  const includeStoreSwitcher =
    stores?.length !== undefined && stores?.length > 1;

  const isDevMode = process.env['NODE_ENV'] == 'development';
  const { mutate } = useCreateDevUserSession();

  const handleSignInWithControls = useCallback(() => {
    mutate({ email: undefined, password: undefined });
  }, [mutate]);

  return (
    <StyledContainer>
      <ThemedBackground
        style={topHeaderStyle(includeStoreSwitcher || showSignIn)}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          mt={getKioskSpacing(24, 8)}
          mb={getKioskSpacing(16, 12)}
          mx={getKioskSpacing(64, 48)}
        >
          {includeStoreSwitcher && (
            <StoreSwitcher
              options={stores}
              currentStore={store}
              onClick={onStoreSwitchClick}
            />
          )}
          {isDevMode && showSignIn && !signedIn && (
            <Button
              label="dev user sign in"
              size="small"
              onClick={handleSignInWithControls}
              variant="tertiary"
            />
          )}
          {!signedIn && (
            <SignInPrompt onClick={() => onLoginIconClick('sign in prompt')} />
          )}
          {showSignIn && !signedIn && (
            <SignInButton
              location="menu"
              ml="auto"
              onLoginIconClick={() => onLoginIconClick('login icon')}
              isJaneMode={isJaneMode}
            />
          )}
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          mx={getKioskSpacing(64, 48)}
          my={getKioskSpacing(24, 16)}
        >
          <Flex flexDirection="column">
            <Typography
              as="h1"
              color={isJaneMode ? 'text-main' : 'text-info'}
              variant={isIpad ? 'h3-regular' : 'h2-regular'}
            >
              Welcome to
            </Typography>
            <Typography
              as="h2"
              color={isJaneMode ? 'text-main' : 'text-info'}
              maxLines={2}
              variant={isIpad ? 'h2-semibold' : 'h1-regular'}
            >
              {kioskName || store.name}
            </Typography>
          </Flex>
          <Flex grow={1} justifyContent="flex-end">
            <HeaderIcons
              cart={cart}
              onCartButtonClick={onCartButtonClick}
              onLoginIconClick={() => onLoginIconClick('login icon')}
              onLogoutIconClick={onLogoutIconClick}
              onSearchIconClick={onSearchIconClick}
              signedIn={signedIn}
              storeId={store.id}
            />
          </Flex>
        </Flex>
      </ThemedBackground>
      <MenuCategories
        ref={categoriesRef}
        isJaneMode={isJaneMode}
        handleCategorySelected={onCategoryPress}
        store={store}
        {...menuCategoriesProps}
      />
    </StyledContainer>
  );
};
